import React, {
    createContext,
    useState,
    useRef,
    useMemo,
    useContext,
    useEffect,
} from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Layout from '../../components/Layout'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import {
    Container,
    Row,
    Column,
    colors,
    gutter,
    mediaQueries,
    themes,
} from '../../styles'
import Section from '../../components/Section'
import Button from '../../components/Button'
import StateSelect from '../../components/Forms/StateSelect'
import Input from '../../components/Forms/Input'
import LogoSvg from '../../images/logo.inline.svg'
import { ErrorMessage } from '../../components/Forms/FormMessage'
import { submitForm } from '../../utils'
import TransitionLink from 'gatsby-plugin-transition-link'
import Loader from '../../components/UI/Loader'
import Hamburger from '../../components/Header/Hamburger'
import Footprint from '../../images/footprint.inline.svg'

// https://seifi.org/aws/build-a-contact-form-in-gatsby-part-1-aws-lambda-simple-email-service-and-api-gateway.html

const FootprintContext = createContext({
    footprint: '',
    setFootprint: () => {},
})

const S = {
    Aside: styled.aside`
        background-color: ${colors.dark};
        padding: 5vh 10px;

        ${mediaQueries.sm} {
            position: fixed;
            top: 0;
            left: 0;
            height: 100%;
            width: 25%;
        }

        ${mediaQueries.md} {
            padding: 5vh;
        }
    `,
    Main: styled.main`
        padding: 5vh 30px;
        min-height: 100vh;
        ${mediaQueries.sm} {
            padding: 10vh 30px 5vh calc(25% + 30px);
        }
    `,
    Logo: styled(LogoSvg)`
        fill: white;
        height: auto;
        width: 100%;
        max-width: 200px;
        margin: 0 auto;
        display: block;

        ${mediaQueries.sm} {
            margin-bottom: 50px;
        }
    `,
    StepWrapper: styled.div`
        position: relative;
        line-height: 1;
    `,
    StepRoot: styled.div`
        display: flex;
        align-items: center;
        color: white;
        flex-flow: row wrap;
    `,
    StepIcon: styled.div`
        display: flex;
        flex-shrink: 0;
        padding-right: 10px;
        color: ${(props) => (props.active ? colors.compliment : `white`)};

        svg {
            width: 1.25em;
            height: 1.25em;
            fill: currentColor;

            text {
                font-size: 0.75em;
            }
        }
    `,
    StepLabel: styled.label`
        display: block;
        font-size: 1.25rem;
    `,
    StepData: styled.ul`
        width: 100%;
        padding-left: 1.25em;
        margin-left: 10px;
        margin-top: 20px;

        li {
            font-size: 0.85em;
            margin-bottom: 10px;
            transform-origin: center top;
        }
    `,
    StepsWrapper: styled.div`
        max-height: 0px;
        overflow: hidden;
        transition: all 0.8s;

        &.open {
            padding-top: 50px;
            max-height: unset;
        }

        ${mediaQueries.sm} {
            &,
            &.open {
                max-height: unset;
                padding-top: 0px;
            }
        }
    `,
    Divider: styled.div`
        padding-left: 0.625em;
        width: 100%;
        position: absolute;
        left: 0;
        top: 1.25em;
        bottom: 0px;

        svg {
            width: 1.25em;
            height: 100%;
        }
    `,
    SelectBox: styled.div`
        position: relative;

        &:hover > label {
            color: ${colors.compliment};
        }

        input {
            position: absolute;
            height: 100%;
            width: 100%;
            top: 0;
            left: 0;
            opacity: 0;
            pointer-events: none;

            &:checked + label {
                color: ${colors.compliment};
            }
        }

        label {
            padding: 20px ${gutter};
            color: black;
            border: 2px solid currentColor;
            text-align: center;
            cursor: pointer;
            transition: all 0.3s;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: center;

            svg {
                width: 100%;
                max-width: 100px;
                height: 100px;
                fill: currentColor;
            }

            span {
                display: block;
                margin-top: 1em;
            }
        }
    `,
    Icon: styled(motion.svg)`
        height: 3.5em;
        width: 3.5em;
    `,
}

const HamburgerColumn = styled(Column)`
    text-align: right;
    ${mediaQueries.sm} {
        display: none;
    }
`

const draw = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
        pathLength: 1,
        opacity: 1,
        transition: {
            delay: 1,
            pathLength: { type: 'spring', duration: 1.5, bounce: 0 },
            opacity: { duration: 0.01 },
        },
    },
}

const ElectricityIcon = () => (
    <S.Icon
        viewBox="0 0 20 35"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
    >
        <g
            transform="translate(1.000000, 1.000000)"
            stroke="#000000"
            strokeWidth="1"
            fill="none"
            fillRule="evenodd"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <motion.path
                variants={draw}
                d="M9,33 C7.0128,33 5.4,31.3573333 5.4,29.3333333 L5.4,23.2228333 C5.4,21.2391667 4.7682,19.3086667 3.6,17.7228333 L1.8,15.2771667 C0.6318,13.6913333 0,11.7608333 0,9.77716667 L0,9.16666667 C0,4.10483333 4.0302,0 9,0 C13.9698,0 18,4.10483333 18,9.16666667 L18,9.77716667 C18,11.7608333 17.3682,13.6913333 16.2,15.2771667 L14.4,17.7228333 C13.2318,19.3086667 12.6,21.2391667 12.6,23.2228333 L12.6,29.3333333 C12.6,31.3573333 10.9872,33 9,33 Z M5.4,25.6666667 L12.6,25.6666667 M5.4,29.3333333 L12.6,29.3333333 M9,25.6666667 L9,12.8333333"
            ></motion.path>
        </g>
    </S.Icon>
)

const NatGasIcon = () => (
    <motion.svg
        viewBox="0 0 44 44"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
    >
        <g
            strokeWidth="1"
            fill="none"
            transform="translate(1.000000, 1.000000)"
            fillRule="nonzero"
            stroke="#000000"
        >
            <motion.path
                variants={draw}
                d="M3.64777328,1.28440367 L4.6194332,1.28440367 L4.6194332,6.93577982 C5.00809717,6.67889908 5.46153846,6.42201835 5.91497976,6.2293578 L5.91497976,1.28440367 L16.0850202,1.28440367 L16.0850202,6.29357798 C16.5384615,6.48623853 16.9919028,6.74311927 17.3805668,7 L17.3805668,1.28440367 L18.3522267,1.28440367 C18.6761134,1.28440367 19,0.963302752 19,0.642201835 C19,0.256880734 18.6761134,0 18.3522267,0 L3.64777328,0 C3.25910931,0 3,0.256880734 3,0.642201835 C3,0.963302752 3.32388664,1.28440367 3.64777328,1.28440367 Z"
            ></motion.path>
            <motion.path
                variants={draw}
                d="M9.06666667,3 C8.66666667,3 8.4,3.26455026 8.4,3.66137566 L8.4,5.31481481 C9.26666667,5.11640212 10.1333333,5.05026455 11.0666667,5.05026455 C12,5.05026455 12.8666667,5.11640212 13.7333333,5.31481481 L13.7333333,3.66137566 C13.7333333,3.26455026 13.4666667,3 13.0666667,3 L9.06666667,3 Z M11,6.37301587 C5.66666667,6.37301587 1.2,10.010582 0,14.9708995 L22,14.9708995 C20.8666667,10.010582 16.3333333,6.37301587 11,6.37301587 Z M8.8,26.1481481 C8.8,27.1402116 9.8,28 11.0666667,28 C12.3333333,28 13.3333333,27.2063492 13.3333333,26.1481481 C13.3333333,25.6190476 12.5333333,23.8333333 11.0666667,21.2539683 C9.6,23.7671958 8.8,25.6190476 8.8,26.1481481 Z"
            ></motion.path>
            <motion.path
                variants={draw}
                d="M0.131455399,16.5950704 C0.0657276995,17.0510563 0.0657276995,17.4419014 0.0657276995,17.8978873 L0.0657276995,31.7077465 L22.4131455,31.7077465 L22.4131455,17.9630282 C22.4131455,17.5070423 22.3474178,17.0510563 22.3474178,16.6602113 L0.131455399,16.6602113 L0.131455399,16.5950704 Z M0,36.0070423 C0,37.5704225 1.31455399,38.8732394 2.89201878,38.8732394 L4.07511737,38.8732394 L4.07511737,40.0457746 C4.07511737,41.153169 4.92957746,42 6.04694836,42 L16.3661972,42 C17.4178404,42 18.3380282,41.153169 18.3380282,40.0457746 L18.3380282,38.8732394 L19.5211268,38.8732394 C21.0985915,38.8732394 22.4131455,37.5704225 22.4131455,36.0070423 L22.4131455,33.0757042 L0.0657276995,33.0757042 L0.0657276995,36.0070423 L0,36.0070423 Z M29.9061033,9.55985915 L30.4319249,9.55985915 L30.4319249,11.0580986 C31.1549296,10.8626761 31.943662,10.7323944 32.7323944,10.7323944 C33.5211268,10.7323944 34.3098592,10.8626761 35.0328638,11.0580986 L35.0328638,9.55985915 L35.5586854,9.55985915 C35.8873239,9.55985915 36.2159624,9.29929577 36.2159624,8.9084507 C36.2159624,8.58274648 35.8873239,8.25704225 35.5586854,8.25704225 L35.0328638,8.25704225 L35.0328638,7.27992958 C35.0328638,6.04225352 33.9812207,5 32.7323944,5 C31.4835681,5 30.4319249,6.04225352 30.4319249,7.27992958 L30.4319249,8.25704225 L29.9061033,8.25704225 C29.5117371,8.25704225 29.2488263,8.58274648 29.2488263,8.9084507 C29.2488263,9.23415493 29.5117371,9.55985915 29.9061033,9.55985915 Z M40.5539906,20.5035211 L24.9107981,20.5035211 L24.9107981,33.4665493 L40.6197183,33.4665493 L40.6197183,20.5035211 L40.5539906,20.5035211 Z M32.7323944,30.8609155 C31.286385,30.8609155 30.1032864,29.818662 30.1032864,28.5809859 C30.1032864,27.5387324 31.8122066,24.6725352 32.4694836,23.5 C32.600939,23.3045775 32.8638498,23.3045775 32.9953052,23.5 C33.6525822,24.6073944 35.3615023,27.5387324 35.3615023,28.5809859 C35.3615023,29.8838028 34.1784038,30.8609155 32.7323944,30.8609155 Z M40.5539906,38.0915493 L40.5539906,34.7693662 L24.9107981,34.7693662 L24.9107981,38.0915493 C24.1220657,38.3521127 23.5962441,39.068662 23.5962441,39.915493 C23.5962441,41.0228873 24.5164319,41.8697183 25.5680751,41.8697183 L40.028169,41.8697183 C41.1455399,41.8697183 42,41.0228873 42,39.915493 C41.9342723,39.068662 41.342723,38.3521127 40.5539906,38.0915493 Z M32.7323944,12.1003521 C28.657277,12.1003521 25.2394366,15.2271127 24.9107981,19.2658451 L40.5539906,19.2658451 C40.2253521,15.2271127 36.8075117,12.1003521 32.7323944,12.1003521 Z"
            ></motion.path>
        </g>
    </motion.svg>
)

const HeatOilIcon = () => (
    <motion.svg
        viewBox="0 0 113 123"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
    >
        <g
            strokeWidth="3"
            transform="translate(1.000000, 1.000000)"
            fillRule="nonzero"
            stroke="#000000"
            fill="none"
        >
            <motion.path
                variants={draw}
                d="M105.553426,110.106138 C103.654426,112.008938 101.765226,114.540238 101.765126,116.770738 C101.765126,119.25602 103.779844,121.270738 106.265126,121.270738 C108.750407,121.270738 110.765126,119.25602 110.765126,116.770738 C110.765126,114.540238 108.876026,112.008938 106.976926,110.106038 C106.788441,109.916726 106.532298,109.810329 106.265155,109.810329 C105.998012,109.810329 105.741884,109.9168 105.553426,110.106138 L105.553426,110.106138 Z"
            ></motion.path>
            <motion.rect
                variants={draw}
                transform="translate(70.481819, 24.974631) rotate(-45.001000) translate(-70.481819, -24.974631) "
                x="67.5678186"
                y="12.9746313"
                width="5.828"
                height="24"
                rx="2"
            ></motion.rect>
            <motion.path
                variants={draw}
                d="M105.553526,97.1061384 C103.654426,99.0089384 101.765226,101.540238 101.765126,103.770738 C101.765126,106.25602 103.779844,108.270738 106.265126,108.270738 C108.750407,108.270738 110.765126,106.25602 110.765126,103.770738 C110.765126,101.540738 108.876026,99.0090384 106.976926,97.1062384 C106.788498,96.9168813 106.532382,96.8103962 106.265247,96.8103962 C105.998111,96.8103962 105.74198,96.9168078 105.553526,97.1061384 L105.553526,97.1061384 Z M103.128126,95.3484384 L109.376626,92.7602384 C109.9579,92.5194808 110.392,92.0192337 110.548464,91.4098385 C110.704929,90.8004433 110.565517,90.1529448 110.172126,89.6619384 L99.9361257,76.8860384 C96.8313609,73.0108026 92.3960268,70.4291117 87.4930257,69.6432384 L75.8507257,67.7772384 C75.5398746,67.7274195 75.2706319,67.5341261 75.1240244,67.2555285 C74.9774169,66.976931 74.9705631,66.6455597 75.1055257,66.3611384 L78.7338257,58.7143384 C80.5464381,54.8940745 79.7602855,50.3466416 76.7703257,47.3566384 L70.4744257,41.0607384 C69.6933952,40.2796929 69.6933952,39.0133839 70.4744257,38.2323384 L73.3612257,35.3453384 L60.6332257,22.6176384 L57.7464257,25.5043384 C56.9653802,26.2853689 55.6990712,26.2853689 54.9180257,25.5043384 L32.3305257,2.91683837 C28.4186313,-0.972279458 22.1003202,-0.972279458 18.1884257,2.91683837 L0.568425722,20.5368384 C0.204474925,20.9006762 7.10542736e-15,21.3942141 7.10542736e-15,21.9088384 C7.10542736e-15,22.4234627 0.204474925,22.9170005 0.568425722,23.2808384 L0.603725722,23.3162384 C0.977379274,23.6942064 1.48621602,23.9077423 2.01769818,23.9096189 C2.54918034,23.9114955 3.05951235,23.7015583 3.43582572,23.3262384 L21.0208257,5.74123837 C22.1458438,4.61653153 23.6717297,3.98504459 25.2625253,3.98581357 C26.8533209,3.98658255 28.3785955,4.6195444 29.5025257,5.74533837 L33.2666257,9.50943837 C33.657141,9.89996111 33.657141,10.5331156 33.2666257,10.9236384 L23.4866257,20.7036384 L12.7601257,31.4298384 C10.8847422,33.3052048 9.83116135,35.8487577 9.83116135,38.5009384 C9.83116135,41.153119 10.8847422,43.696672 12.7601257,45.5720384 L45.0092257,77.8211384 C48.6275855,81.4391437 53.4950152,83.5327311 58.6100257,83.6711384 L87.2559257,84.4467384 C89.9737432,84.5203242 92.5444415,85.6973574 94.3759257,87.7067384 L100.884926,94.8476384 C101.4525,95.4704559 102.349555,95.6707257 103.128126,95.3484384 Z"
            ></motion.path>
        </g>
    </motion.svg>
)

const SqFtIcon = () => (
    <motion.svg
        viewBox="0 0 23 25"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
    >
        <g
            transform="translate(1.000000, 1.000000)"
            stroke="#000000"
            strokeWidth=".5"
            fill="none"
            fillRule="evenodd"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <motion.path
                variants={draw}
                d="M4,17 L4,9.657 L11.343,17 L4,17 Z M3,21 L3,19.5 M6,21 L6,20 M9,21 L9,19.5 M12,21 L12,20 M13,6 L19,6 M16,6 L16,19 M13,19 L19,19 M19,19 L16,23 L13,19 L13,3.5 C13,2.67157288 13.6715729,2 14.5,2 L17.5,2 C18.3284271,2 19,2.67157288 19,3.5 L19,19 Z M17.5,21 L21,21 L19,19"
            ></motion.path>
            <motion.polyline
                variants={draw}
                points="13 13 0 0 0 21 14.5 21"
            ></motion.polyline>
        </g>
    </motion.svg>
)

const steps = [
    {
        title: 'Property Type',
    },
    {
        title: 'Address',
    },
    {
        title: 'Contact',
    },
]

const listVariants = {
    initial: {
        scaleY: 0,
    },
    animate: {
        scaleY: 1,
    },
    exit: {
        scaleY: 0,
    },
}

const Step = ({ index, title, active, last, data }) => {
    const dataMap = data !== null ? Object.values(data) : []

    return (
        <S.StepWrapper>
            <S.StepRoot>
                <S.StepIcon active={active}>
                    <svg
                        focusable="false"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="12"></circle>
                        <text
                            x="12"
                            y="16"
                            textAnchor="middle"
                            fill={active ? 'white' : colors.dark}
                        >
                            {index + 1}
                        </text>
                    </svg>
                </S.StepIcon>
                <S.StepLabel>{title}</S.StepLabel>
                <S.StepData>
                    <AnimatePresence exitBeforeEnter initial={false}>
                        {dataMap.length > 0 &&
                            dataMap.map((item, index) => (
                                <motion.li
                                    key={index}
                                    variants={listVariants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    transition={{
                                        duration: 0.75,
                                        ease: [0.85, 0, 0.15, 1],
                                    }}
                                >
                                    {item}
                                </motion.li>
                            ))}
                    </AnimatePresence>
                </S.StepData>
            </S.StepRoot>
            {!last && (
                <S.Divider>
                    <svg
                        viewBox="0 0 24 100"
                        aria-hidden="true"
                        preserveAspectRatio="none"
                    >
                        <line x1="0" y1="20" x2="0" y2="80" stroke="white" />
                    </svg>
                </S.Divider>
            )}
        </S.StepWrapper>
    )
}

const transformedValue = (val) =>
    val
        .replace(/_/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()))

const EnergySection = ({ callBack }) => {
    const { handleSubmit, register } = useForm()
    const { setFootprint } = useContext(FootprintContext)

    const onSubmit = (data) => {
        setFootprint(data)
        let values = {}
        for (const [key, value] of Object.entries(data)) {
            const prepend = key.includes('bill') ? `$` : ``
            values[key] = `${prepend}${value} ${transformedValue(key)}`
        }
        callBack(values)
    }

    return (
        <form method="post" onSubmit={handleSubmit(onSubmit)}>
            <Row>
                <Column sm={50}>
                    <Row alignX="space-between">
                        <Column sm={15} style={{ textAlign: 'center' }}>
                            <ElectricityIcon />
                        </Column>
                        <Column sm={85} style={{ marginBottom: 20 }}>
                            <h3
                                style={{ marginBottom: 10, fontSize: '1.5rem' }}
                            >
                                Avg. Monthly Electricity Bill:
                            </h3>
                            <Input
                                id="avg_monthly_electricity_bill"
                                type="number"
                                defaultValue={55}
                                register={register}
                                required={true}
                                style={{ padding: 20 }}
                            />
                        </Column>
                    </Row>
                </Column>
                <Column sm={50}>
                    <Row alignX="space-between">
                        <Column sm={15} style={{ textAlign: 'center' }}>
                            <NatGasIcon />
                        </Column>
                        <Column sm={85} style={{ marginBottom: 20 }}>
                            <h3
                                style={{ marginBottom: 10, fontSize: '1.5rem' }}
                            >
                                Avg. Monthly Natural Gas Bill:
                            </h3>
                            <Input
                                id="avg_monthly_natural_gas_bill"
                                type="number"
                                defaultValue={25}
                                register={register}
                                required={true}
                                style={{ padding: 20 }}
                            />
                        </Column>
                    </Row>
                </Column>
                <Column sm={50}>
                    <Row alignX="space-between">
                        <Column sm={15} style={{ textAlign: 'center' }}>
                            <HeatOilIcon />
                        </Column>
                        <Column sm={85} style={{ marginBottom: 20 }}>
                            <h3
                                style={{ marginBottom: 10, fontSize: '1.5rem' }}
                            >
                                Avg. Monthly Bill from Heating Oil &amp; Other
                                Fuels:
                            </h3>
                            <Input
                                id="avg_monthly_bill_from_heating_oil_and_other_fuels"
                                type="number"
                                defaultValue={20}
                                register={register}
                                required={true}
                                style={{ padding: 20 }}
                            />
                        </Column>
                    </Row>
                </Column>
                <Column sm={50}>
                    <Row alignX="space-between">
                        <Column sm={15} style={{ textAlign: 'center' }}>
                            <SqFtIcon />
                        </Column>
                        <Column sm={85} style={{ marginBottom: 20 }}>
                            <h3
                                style={{ marginBottom: 10, fontSize: '1.5rem' }}
                            >
                                Sq. Ft. of Living Space:
                            </h3>
                            <Input
                                id="square_feet_of_living_space"
                                type="number"
                                defaultValue={20}
                                register={register}
                                required={true}
                                style={{ padding: 20 }}
                            />
                        </Column>
                    </Row>
                </Column>
            </Row>
            <Button as="button" type="submit">
                NEXT
            </Button>
        </form>
    )
}

const AddressSection = ({ callBack }) => {
    const { handleSubmit, register } = useForm()

    const onSubmit = (data) => callBack(data)

    return (
        <form method="post" onSubmit={handleSubmit(onSubmit)}>
            <Section size="tiny">
                <h3>Enter Your Address</h3>
                <Input
                    id="address"
                    label="Enter Your Address"
                    register={register}
                    required={true}
                />
            </Section>
            <Section size="tiny">
                <h3>Select the State You Live In</h3>
                <StateSelect name="state" required={true} register={register} />
            </Section>
            <Section size="tiny">
                <h3>Enter Your Zip Code</h3>
                <Input
                    id="zip_code"
                    label="Enter Your Zip Code"
                    register={register}
                    required={true}
                />
            </Section>
            <Button as="button" type="submit">
                NEXT
            </Button>
        </form>
    )
}

const ContactSection = ({ callBack }) => {
    const { handleSubmit, register } = useForm()

    const onSubmit = (data) => callBack(data)

    return (
        <form method="post" onSubmit={handleSubmit(onSubmit)}>
            <Section size="tiny">
                <h3>Enter Your Full Name</h3>
                <Row>
                    <Column sm={50}>
                        <Input
                            id="first_name"
                            label="First Name"
                            register={register}
                            required={true}
                        />
                    </Column>
                    <Column sm={50}>
                        <Input
                            id="last_name"
                            label="Last Name"
                            register={register}
                            required={true}
                        />
                    </Column>
                </Row>
            </Section>
            <Section size="tiny">
                <h3>Enter Your Phone Number</h3>
                <Input
                    id="phone"
                    type="tel"
                    label="Phone Number"
                    register={register}
                    required={true}
                />
            </Section>
            <Section size="tiny">
                <h3>Enter Your Email Address</h3>
                <Input
                    id="email"
                    type="email"
                    label="Email Address"
                    register={register}
                    required={true}
                />
            </Section>
            <Button as="button" type="submit">
                CALCULATE YOUR FOOTPRINT
            </Button>
        </form>
    )
}

const variants = {
    initial: {
        opacity: 0,
        y: 100,
    },
    animate: {
        opacity: 1,
        y: 0,
    },
    exit: {
        opacity: 0,
        y: -100,
    },
}

const components = [EnergySection, AddressSection, ContactSection]

const Nav = ({ step, data }) => {
    const ref = useRef(null)

    const openNav = (e) => {
        if (ref.current) {
            if (e === true) {
                ref.current.classList.add('open')
            } else {
                ref.current.classList.remove('open')
            }
        }
    }

    return (
        <S.Aside>
            <Row alignX="center">
                <Column xs={50} sm={90}>
                    <TransitionLink
                        to="/"
                        exit={{ length: 1.2, zIndex: 2 }}
                        entry={{ length: 1.2, zIndex: 1 }}
                    >
                        {' '}
                        <S.Logo />
                    </TransitionLink>
                </Column>
                <HamburgerColumn xs={50}>
                    <Hamburger
                        theme={themes.light}
                        callback={(e) => openNav(e)}
                    />
                </HamburgerColumn>
            </Row>
            <S.StepsWrapper ref={ref}>
                {steps.length &&
                    steps.map((item, index) => (
                        <Step
                            title={item.title}
                            index={index}
                            key={index}
                            active={index === step}
                            last={index >= steps.length - 1}
                            data={
                                typeof data[index] !== 'undefined'
                                    ? data[index]
                                    : null
                            }
                        />
                    ))}
            </S.StepsWrapper>
        </S.Aside>
    )
}

const calculateFootprint = (vals) => {
    const {
        avg_monthly_electricity_bill: electric,
        avg_monthly_natural_gas_bill: gas,
        avg_monthly_bill_from_heating_oil_and_other_fuels: other,
    } = vals
    const footprint = 105 * electric + 105 * gas + 113 * other
    return footprint
}

const SuccessMessage = ({ footprint }) => {
    const rounded = Math.ceil(footprint).toLocaleString('en-US')
    const savings = Math.floor(0.2 * footprint).toLocaleString('en-US')
    return (
        <div style={{ textAlign: 'center' }}>
            <Footprint
                style={{ marginBottom: 30, width: 150, height: 'auto' }}
            />
            <h3>
                Your current footprint is approximately{' '}
                <span style={{ color: colors.compliment }}>
                    {rounded} lbs./year
                </span>
                , but you can lower it down to{' '}
                <span style={{ color: colors.compliment }}>{savings}</span> by
                switching to solar.
            </h3>
            <p>Our team will be in touch shortly to get the process started!</p>
        </div>
    )
}

const SavingsForm = () => {
    const [step, setStep] = useState(0)
    const [data, setData] = useState({})
    const [result, setResult] = useState(false)
    const { footprint } = useContext(FootprintContext)
    const calculatedFootprint = useMemo(
        () => (footprint !== '' ? calculateFootprint(footprint) : ''),
        [footprint]
    )

    useEffect(() => {
        if (
            Object.keys(data).length === components.length &&
            step === components.length - 1
        ) {
            let fields = {}
            Object.entries(data).forEach(([index, values]) =>
                Object.assign(fields, values)
            )
            submitForm({
                form: 'Environment',
                fields: fields,
            }).then((response) => {
                if (
                    typeof response.ok !== 'undefined' &&
                    response.ok === true
                ) {
                    setResult('success')
                    setStep(components.length)
                } else {
                    setResult('error')
                }
            })
            //console.log(data)
        }
    }, [data, step])

    const callBack = (vals, page) => {
        const max = components.length - 1
        setData({ ...data, [page]: { ...vals } })
        if (page < max) {
            setStep(step + 1)
        } else {
            setResult('submitting')
        }
    }

    return (
        <>
            <Nav step={step} data={data} />
            <S.Main>
                <Container>
                    {result !== 'success' && (
                        <>
                            <h1
                                style={{ fontSize: '2.5rem', marginBottom: 20 }}
                            >
                                Calculate Your Carbon Footprint
                            </h1>
                            <p style={{ fontSize: '1.5rem', marginBottom: 50 }}>
                                And How Much You Can Reduce It By Switching to
                                Solar
                            </p>
                        </>
                    )}
                    <AnimatePresence exitBeforeEnter initial={false}>
                        {components.length &&
                            components.map(
                                (Item, index) =>
                                    step === index && (
                                        <motion.div
                                            key={index}
                                            variants={variants}
                                            initial="initial"
                                            animate="animate"
                                            exit="exit"
                                            transition={{
                                                duration: 0.75,
                                                ease: [0.85, 0, 0.15, 1],
                                            }}
                                        >
                                            <Item
                                                callBack={(e) => {
                                                    callBack(e, index)
                                                }}
                                            />
                                        </motion.div>
                                    )
                            )}
                        {result === 'success' && (
                            <motion.div
                                key="success"
                                variants={variants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={{
                                    duration: 0.75,
                                    ease: [0.85, 0, 0.15, 1],
                                }}
                            >
                                <SuccessMessage
                                    footprint={calculatedFootprint}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                    {result === 'submitting' && <Loader />}
                    {result === 'error' && (
                        <ErrorMessage>
                            There has been an error and we are unable to process
                            your form. Please try again later.
                        </ErrorMessage>
                    )}
                </Container>
            </S.Main>
        </>
    )
}

// markup
const SavingsPage = () => {
    const [footprint, setFootprint] = useState('')
    const value = useMemo(() => ({ footprint, setFootprint }), [footprint])
    return (
        <Layout showHeader={false} showFooter={false}>
            <FootprintContext.Provider value={value}>
                <SavingsForm />
            </FootprintContext.Provider>
        </Layout>
    )
}

export default SavingsPage
